import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";

import { SidebarCol } from "../../screens/chatMain/Sidebar/Sidebar";
import { MainHeader } from "../../screens/chatMain/MainHeader/MainHeader";
import DailyCheckinDialog from "../../components/checkin/DailyCheckinDialog";
import GrowthProgressDialog from "../../components/checkin/GrowthProgressDialog";
import ReflectYourDayDialog from "../../components/checkin/ReflectYourDayDialog";
import StressReliefDialog from "../../components/stress-relief/StressReliefDialog";
import Landing_Bg from "../../assets/Mindsherpa_landing_bg.png";
import Profile1 from "../../assets/Profile1.png";
import Profile2 from "../../assets/Profile2.png";
import Profile3 from "../../assets/Profile.svg";
import Profile4 from "../../assets/Profile3.png";
import { isAdminUser } from "../../utils/userUtils";
import { toast } from "react-toastify";
import GuidanceAudioPlayer from "../../components/checkin/GuidanceAudioPlayer";
import { audioSources } from "../../constants/checkinQuestions";

import useSubmitCheckin from "../../hooks/checkins/useSubmitCheckin";
import { getTodayCheckinStatus } from "../../api/checkins/checkinsApi";
import {
  buildMorningCheckinPayload,
  buildMiddayCheckinPayload,
  buildNightCheckinPayload,
} from "../../utils/checkins/buildCheckinPayload";

import CheckinsSection from "../../components/checkin/CheckinsSection";

import "./TourLanding.css";

const populateFormValues = (answers, initialFormValues) => {
  if (!answers) return initialFormValues;

  const DOMAIN_ENUM_BY_INDEX = [
    "PHYSICAL",
    "EMOTIONAL",
    "ACCEPTANCE",
    "RELATIONSHIPS",
    "LOVING-KINDNESS",
    "INTELLECTUAL",
  ];

  const morning = answers.morning || {};
  const midday = answers.midday || {};
  const night = answers.night || {};

  const domainStr = morning.q4;
  const domainIndex = DOMAIN_ENUM_BY_INDEX.indexOf(domainStr);

  return {
    daily: {
      mood: morning.q1 ? String(morning.q1 - 1) : "",
      sleep: morning.q2 ? String(morning.q2 - 1) : "",
      stress: morning.q3 ? String(morning.q3 - 1) : "",
      domain: domainIndex !== -1 ? String(domainIndex) : "",
    },
    growth: {
      accomplished: midday.q1 && midday.q1.length ? midday.q1 : [""],
      learned: midday.q2 && midday.q2.length ? midday.q2 : [""],
      enjoyed: midday.q3 && midday.q3.length ? midday.q3 : [""],
    },
    reflect: {
      day_rating: night.q1 ? String(night.q1) : "",
      productivity: night.q2 ? String(night.q2) : "",
      creativity: night.q3 ? String(night.q3) : "",
    },
  };
};

const initialFormValues = {
  daily: { mood: "", sleep: "", stress: "", domain: "" },
  growth: { accomplished: [""], learned: [""], enjoyed: [""] },
  reflect: { day_rating: "", productivity: "", creativity: "" },
};

const TourLanding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = isAdminUser(user);

  const [openDialog, setOpenDialog] = useState(null); // 'daily' | 'growth' | 'reflect' | null
  const { submit, isSubmitting } = useSubmitCheckin();
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null); // 'morning' | 'noon' | 'night'
  const [stressReliefOpen, setStressReliefOpen] = useState(false);
  const storageKey = "dailyCheckinStatus";
  const getTodayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const buildDefaultStatus = (dateKey) => ({
    dateKey,
    morning: false,
    midday: false,
    night: false,
  });

  const loadStatus = () => {
    const todayKey = getTodayKey();
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return buildDefaultStatus(todayKey);
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.dateKey !== todayKey) {
        return buildDefaultStatus(todayKey);
      }
      return {
        dateKey: todayKey,
        morning: !!parsed.morning,
        midday: !!parsed.midday,
        night: !!parsed.night,
      };
    } catch {
      return buildDefaultStatus(todayKey);
    }
  };

  const [checkinStatus, setCheckinStatus] = useState(loadStatus);
  const [formValues, setFormValues] = useState(initialFormValues);

  const syncStatus = React.useCallback(async () => {
    try {
      const todayKey = getTodayKey();
      const response = await getTodayCheckinStatus(todayKey);
      if (!response || !response.success || !response.status) {
        return;
      }

      if (response.date !== todayKey) {
        return;
      }

      const synced = {
        dateKey: todayKey,
        morning: !!response.status.morning,
        midday: !!response.status.midday,
        night: !!response.status.night,
      };

      setCheckinStatus(synced);
      localStorage.setItem(storageKey, JSON.stringify(synced));

      // Populate forms with loaded answers from database
      if (response.answers) {
        setFormValues(populateFormValues(response.answers, initialFormValues));
      }
    } catch (err) {
      console.error("Failed to sync check-in status from backend database:", err);
    }
  }, [initialFormValues]);

  useEffect(() => {
    const fresh = loadStatus();
    setCheckinStatus(fresh);
    localStorage.setItem(storageKey, JSON.stringify(fresh));

    syncStatus();
  }, [syncStatus]);

  const persistStatus = (next) => {
    setCheckinStatus(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const ensureTodayStatus = () => {
    const todayKey = getTodayKey();
    if (checkinStatus.dateKey !== todayKey) {
      const reset = buildDefaultStatus(todayKey);
      persistStatus(reset);
      return reset;
    }
    return checkinStatus;
  };



  const resetForm = (kind) => {
    setFormValues((prev) => ({
      ...prev,
      [kind]: initialFormValues[kind],
    }));
  };

  const handleTakeTour = () => navigate("/tour");
  const handleOpenDialog = (dialogType) => {
    const status = ensureTodayStatus();

    if (dialogType === "growth" && !status.morning) return;
    if (dialogType === "reflect" && !status.midday) return;

    setOpenDialog(dialogType);
  };
  const handleCloseDialog = () => setOpenDialog(null);

  const openGuidance = (sessionType) => {
    setActiveSession(sessionType);
    setCtaOpen(false);
    setGuidanceOpen(true);
  };

  const handleGuidanceComplete = () => {
    setGuidanceOpen(false);
    setCtaOpen(true);
  };

  const handleGuidanceSkip = () => {
    setGuidanceOpen(false);
    setCtaOpen(true);
  };

  const handleCloseCta = () => setCtaOpen(false);

  const handleDailyChange = (newValues) =>
    setFormValues((prev) => ({ ...prev, daily: newValues }));

  const handleGrowthChange = (newValues) =>
    setFormValues((prev) => ({ ...prev, growth: newValues }));

  const handleReflectChange = (newValues) =>
    setFormValues((prev) => ({ ...prev, reflect: newValues }));

   const submitMorning = async () => {
    const status = ensureTodayStatus();
    const payload = {
      ...buildMorningCheckinPayload({ values: formValues.daily }),
      date: status.dateKey,
      checkin_date: status.dateKey,
    };
    await submit(payload);
    toast.success("Morning check-in saved");
    persistStatus({ ...status, morning: true });
    await syncStatus();
  };

  const submitMidday = async () => {
    const status = ensureTodayStatus();
    const payload = {
      ...buildMiddayCheckinPayload({ values: formValues.growth }),
      date: status.dateKey,
      checkin_date: status.dateKey,
    };
    await submit(payload);
    toast.success("Mid-day check-in saved");
    persistStatus({ ...status, midday: true });
    await syncStatus();
  };

  const submitNight = async () => {
    const status = ensureTodayStatus();
    const payload = {
      ...buildNightCheckinPayload({ values: formValues.reflect }),
      date: status.dateKey,
      checkin_date: status.dateKey,
    };
    await submit(payload);
    toast.success("Workday Wrap-Up saved");
    persistStatus({ ...status, night: true });
    await syncStatus();
  };

  const ctaCardSx = {
    borderRadius: 3,
    cursor: "pointer",
    userSelect: "none",
    overflow: "visible",
    minWidth: { xs: "280px", sm: "420px" },
    transition: "transform 160ms ease, box-shadow 160ms ease",
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
    border: `2px solid ${theme.palette.primary[800]}`,
    boxShadow: "0 14px 28px rgba(0,0,0,0.2)",
    transform: "translateY(0px)",
    "&:hover": {
      transform: "translateY(-8px)",
      backgroundImage: `linear-gradient(135deg, ${theme.palette.primary[700]} 0%, ${theme.palette.primary[800]} 100%)`,
      border: `2px solid ${theme.palette.primary[900]}`,
      boxShadow: "0 22px 44px rgba(0,0,0,0.25)",
    },
    "&:active": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
    },
    "&:focus-visible": {
      outline: "3px solid rgba(25,118,210,0.35)",
      outlineOffset: 3,
    },
  };


  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {!isMobile && (
        <Box sx={{ flexShrink: 0 }}>
          <SidebarCol activetab="landing" />
        </Box>
      )}

      <Box
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Mobile Header with Hamburger Menu */}
        {isMobile && (
          <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
            <MainHeader
              titleText="Welcome"
              subtitleText="Your Daily Well-Being Journey"
            />
          </Box>
        )}
        
        {/* Top section with background image */}
        <Box
          sx={{
            minHeight: "12vh",
            maxHeight: "15vh",
            backgroundImage: `url(${Landing_Bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            textAlign="left"
            sx={{
              color: "white",
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              px: 2,
            }}
          >
            Welcome to your daily well-being journey! Get started
          </Typography>
        </Box>

        {/* Bottom section with neutral background */}
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.nuetral?.[50] || "#F8FAFC",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            py: { xs: 2, md: 3 },
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ p: 0, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: { xs: 2, md: 2.5 },
                pr: { xs: 0, sm: 2, md: 4 },
              }}
            >
              {/* Main Tour Button */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Card
                  role="button"
                  tabIndex={0}
                  elevation={8}
                  sx={ctaCardSx}
                  onClick={handleTakeTour}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleTakeTour();
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ fontSize: 40 }}>{isAdmin ? "🗺️" : "🧭"}</Box>
                      <Stack spacing={0.5} alignItems="flex-start" sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: "white" }}>
                          {isAdmin
                            ? "Take your Mindsherpa Administrator tour"
                            : "Take your Mindsherpa tour"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                          Discover features we provide
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Card
                  role="button"
                  tabIndex={0}
                  elevation={8}
                  sx={ctaCardSx}
                  onClick={() => setStressReliefOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setStressReliefOpen(true);
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ fontSize: 36, color: "white", display: "flex", alignItems: "center" }}>
                        <MusicNoteIcon fontSize="inherit" />
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-start" sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: "white" }}>
                          Stress Relief & Focus Music
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                          Start calming focus audio
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>

              <Box sx={{ width: "100%", maxWidth: 980 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "black",
                    fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    lineHeight: 1.5,
                    whiteSpace: "pre-line",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                      fontSize: "1.05rem",
                    }}
                  >
                    Start here:
                  </span>{" "}
                  {`The check-in methodology uses three distinct daily 3-minute moments—Morning Focus, Mid-Day Check-in, and Workday Wrap-Up—to encourage you to pause, reflect, and recharge. This structured approach uses intention setting and well-being scoring to foster growth, anchor you to the present moment, and maximize performance. Each check-in is followed by guidance to reinforce positive mental practices, with your statistics page offering personalized insights for continuous improvement`}
                </Typography>
              </Box>

              <CheckinsSection
                titleTooltip="Commit just 3 minutes daily to maximize your focus and progress"
                onOpen={handleOpenDialog}
                isMorningCompleted={checkinStatus.morning}
                isMiddayCompleted={checkinStatus.midday}
                isNightCompleted={checkinStatus.night}
                allCompleted={
                  checkinStatus.morning &&
                  checkinStatus.midday &&
                  checkinStatus.night
                }
              />

              <div className="tour-footer">
                <p className="tour-footer-text">
                  Mental Clarity: Setting a "Morning Focus" helps users filter out distractions and prioritize their top goals for the day.
                </p>
                <div className="tour-profile-footer">
                  <div className="tour-people">
                    <img src={Profile1} alt="" className="tour-people-img" />
                    <img src={Profile2} alt="" className="tour-people-img" />
                    <img src={Profile3} alt="" className="tour-people-img" />
                    <img src={Profile4} alt="" className="tour-people-img" />
                    <p>+218</p>
                  </div>
                </div>
              </div>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Dialogs */}
      <DailyCheckinDialog
        open={openDialog === "daily"}
        onClose={handleCloseDialog}
        values={formValues.daily}
        onChange={handleDailyChange}
        onComplete={submitMorning}
        onAfterComplete={() => openGuidance("morning")}
        submitting={isSubmitting}
        isCompleted={checkinStatus.morning}
      />

      <GrowthProgressDialog
        open={openDialog === "growth"}
        onClose={handleCloseDialog}
        values={formValues.growth}
        onChange={handleGrowthChange}
        onComplete={submitMidday}
        onAfterComplete={() => openGuidance("noon")}
        submitting={isSubmitting}
        isCompleted={checkinStatus.midday}
      />

      <ReflectYourDayDialog
        open={openDialog === "reflect"}
        onClose={handleCloseDialog}
        values={formValues.reflect}
        onChange={handleReflectChange}
        onComplete={submitNight}
        onAfterComplete={() => openGuidance("night")}
        submitting={isSubmitting}
        isCompleted={checkinStatus.night}
      />

      <StressReliefDialog
        open={stressReliefOpen}
        onClose={() => setStressReliefOpen(false)}
      />

      {/* Guidance Modal */}
      <Dialog
        open={guidanceOpen}
        onClose={handleGuidanceSkip}
        maxWidth="sm"
        fullWidth
        aria-labelledby="guidance-modal-title"
        aria-describedby="guidance-modal-description"
      >
        <DialogTitle id="guidance-modal-title">
          Guidance
        </DialogTitle>
        <DialogContent id="guidance-modal-description">
          <GuidanceAudioPlayer
            audioSrc={
              activeSession === "morning"
                ? audioSources.dailyCheckin
                : activeSession === "noon"
                ? audioSources.growthProgress
                : audioSources.reflectDay
            }
            title={
              activeSession === "morning"
                ? "Morning Focus Check-in Guidance"
                : activeSession === "noon"
                ? "Mid-Day Check-in Guidance"
                : "Workday Wrap-Up Guidance"
            }
            onComplete={handleGuidanceComplete}
            onSkip={handleGuidanceSkip}
          />
        </DialogContent>
      </Dialog>

      {/* CTA Modal */}
      <Dialog
        open={ctaOpen}
        onClose={handleCloseCta}
        maxWidth="sm"
        fullWidth
        aria-labelledby="cta-modal-title"
        aria-describedby="cta-modal-description"
      >
        <DialogTitle id="cta-modal-title">
          {activeSession === "morning"
            ? "Great job on your reflection!"
            : activeSession === "noon"
            ? "Reflection complete!"
            : "Workday Wrap-Up complete!"}
        </DialogTitle>
        <DialogContent id="cta-modal-description">
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body1">
              {activeSession === "morning"
                ? "Great job on your reflection! Register for our lunch-time Reset & Recharge live session."
                : activeSession === "noon"
                ? "Reflection complete! Ready to see your holistic PEARLI scores?"
                : "Explore Mindfulness Practices to Unwind"}
            </Typography>

            <Divider />

            <Button
              variant="text"
              onClick={() => {
                setCtaOpen(false);
                if (activeSession === "night") {
                  navigate("/mindfulness");
                } else if (activeSession === "morning") {
                  navigate("/live");
                } else {
                  navigate("/user-statistics");
                }
              }}
              sx={{
                textTransform: "none",
                alignSelf: "flex-start",
              }}
            >
              {activeSession === "night"
                ? "Start Mindfulness Practice"
                : activeSession === "morning"
                ? "Register for Lunch-Time Reset & Recharge"
                : "See how today impacted your trends"}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {activeSession === "morning" ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setCtaOpen(false);
                  navigate("/live");
                }}
              >
                Register for Live Session
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setCtaOpen(false);
                  navigate("/sherpa");
                }}
              >
                Go to Sherpa
              </Button>
            </>
          ) : activeSession === "noon" ? (
            <Button
              variant="contained"
              onClick={() => {
                setCtaOpen(false);
                navigate("/assessment-dashboard");
              }}
            >
              Take Assessment
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                setCtaOpen(false);
                navigate("/mindfulness");
              }}
            >
              Start Mindfulness Practice
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TourLanding;
